type LatLng = { lat: number; lng: number };
type GeocodeResult = {
    types?: string[];
    geometry?: { location?: LatLng | { lat: () => number; lng: () => number } };
    address_components?: AddressComponent[];
};
type AddressComponent = { types?: string[]; long_name: string };

const isAddrType = (t: string[] = []) =>
    t.includes("street_address") || t.includes("premise") || t.includes("subpremise") || t.includes("intersection");

export const pickBestGeocodeResult = (
    results: GeocodeResult[] = [],
    clickedLatLng?: LatLng
): GeocodeResult | null => {
    if (!results.length) return null;

    const clicked =
        clickedLatLng && isFinite(clickedLatLng.lat) && isFinite(clickedLatLng.lng)
            ? clickedLatLng
            : null;

    const dist = (r: GeocodeResult): number => {
        if (!clicked) return Number.POSITIVE_INFINITY;
        const p = getResultLatLng(r);
        if (!p) return Number.POSITIVE_INFINITY;
        return haversineMeters(clicked, p);
    };

    const routes = results
        .filter((r) => (r.types || []).includes("route"))
        .map((r) => ({ r, d: dist(r), name: getRouteName(r) }))
        .filter((x) => !!x.name)
        .sort((a, b) => a.d - b.d);

    const bestRoute = routes[0]?.r ?? null;
    const bestRouteName = bestRoute ? getRouteName(bestRoute) : "";

    if (bestRouteName) {
        const bestAddrOnSameRoute = results
            .filter((r) => isAddrType(r.types || []) && getRouteName(r) === bestRouteName)
            .map((r) => ({ r, d: dist(r) }))
            .sort((a, b) => a.d - b.d)[0]?.r;
        if (bestAddrOnSameRoute) return bestAddrOnSameRoute;
        return bestRoute;
    }

    const byType = (type: string): GeocodeResult | undefined =>
        results
            .filter((r) => (r.types || []).includes(type))
            .map((r) => ({ r, d: dist(r) }))
            .sort((a, b) => a.d - b.d)[0]?.r;

    return byType("street_address") || byType("premise") || byType("subpremise") || results[0];
};


export const getComponent = (
    components: AddressComponent[] | undefined,
    type: string
): string =>
    components?.find((c) => c.types?.includes(type))?.long_name || "";

export const getRouteName = (result: GeocodeResult): string =>
    getComponent(result?.address_components, "route") || "";

export const haversineMeters = (a: LatLng, b: LatLng): number => {
    const R = 6371000;
    const toRad = (x: number) => (x * Math.PI) / 180;

    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);

    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);

    const s =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

    return 2 * R * Math.asin(Math.sqrt(s));
};

export const getResultLatLng = (
    r: GeocodeResult
): LatLng | null => {
    const loc = r?.geometry?.location;
    if (!loc) return null;

    if (typeof loc.lat === "number" && typeof loc.lng === "number") {
        return { lat: loc.lat, lng: loc.lng };
    }

    if (typeof loc.lat === "function" && typeof loc.lng === "function") {
        return { lat: loc.lat(), lng: loc.lng() };
    }

    return null;
};

export const parseAddressFromComponents = (
    components: AddressComponent[]
): {
    rua: string;
    numero: string;
    cidade: string;
    bairro: string;
    cep: string;
} => {
    const rua = getComponent(components, "route");
    const numeroRaw = getComponent(components, "street_number");
    const numero = (numeroRaw || "").replace(/[^\d]/g, "");
    const cidade =
        getComponent(components, "locality") ||
        getComponent(components, "administrative_area_level_2") ||
        getComponent(components, "administrative_area_level_3") ||
        getComponent(components, "administrative_area_level_4");
    const bairro =
        getComponent(components, "sublocality_level_1") ||
        getComponent(components, "sublocality") ||
        getComponent(components, "neighborhood");
    const cep = getComponent(components, "postal_code").replace("-", "");

    return { rua, numero, cidade, bairro, cep };
};

export const buscarPostalCode = (data: GeocodeResult[]) => {
    for (const item of data) {
        const postalCodeComponent = item.address_components?.find((component) =>
            component?.types?.includes("postal_code")
        );
        if (postalCodeComponent) {
            return postalCodeComponent.long_name;
        }
    }
    return "";
};